import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import UserContext from '../../../context/User/User'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'

import AuthService from '../../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import { unifiedSearch } from '../../../utilities/api'

import {
  formatNumber,
  checkPermission,
  exportData,
} from '../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'

import { NoPermissionsContainer } from '../../NoPermissionsContainer'
import { PageLoadingContainer } from '../../PageLoadingContainer'
import { LoadingContainer } from '../../LoadingContainer'
import { EmptyContainer } from '../../EmptyContainer'
import { Pagination } from '../../Pagination'
import { OrderSummary } from '../../SearchOrdersWrapper/OrdersList/OrderSummary'
import { OrderInfo } from '../../SearchOrdersWrapper/OrdersList/OrdersList'
import { SearchHeader } from '../SearchHeader'

export default function FlexPackHolders({ uuid }) {
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.FLEX_PACKAGES_MANAGEMENT
    )

  const [orders, setOrders] = useState([])
  const [isInitialState, setIsInitialState] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [totalOrders, setTotalOrders] = useState(0)
  const didMountRef = useRef(false)
  const organization = AuthService.getOrg()

  // Hooks for convenience
  const debouncedQuery = useDebouncedValue(searchTerm, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime = false) => {
      setIsLoading(true)
      try {
        const res = await abortable((signal) =>
          unifiedSearch(
            {
              organizationUUID: organization?.[0]?.uuid,
              uuid: uuid,
              by: 'flex-pack',
              searchTerm: debouncedQuery,
              page: currentPage + 1,
              perPage: perPage,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: { pageCount: pc, total },
          },
        } = res.data

        setOrders(data)
        setPageCount(pc)
        setTotalOrders(formatNumber(total))
        setIsLoading(false)
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        setIsInitialState(isFirstTime)
      }
    },
    [abortable, uuid, currentPage, perPage, debouncedQuery, setPageCount]
  )

  const handleExportAll = async () => {
    try {
      const organizationUUID = organization?.[0]?.uuid
      if (!organizationUUID || !uuid) return

      const perPageForExport = 500
      let page = 1
      let exportPageCount = 1
      const allOrders = []

      do {
        const res = await unifiedSearch({
          organizationUUID,
          uuid,
          by: 'flex-pack',
          searchTerm: debouncedQuery,
          page,
          perPage: perPageForExport,
        })

        const responseData = res?.data?.data || []
        const pagination = res?.data?.meta?.pagination || {}
        exportPageCount = Number(pagination.pageCount || 1)

        allOrders.push(...responseData)
        page += 1
      } while (page <= exportPageCount)

      if (!allOrders.length) return

      exportData(
        allOrders,
        'orders',
        undefined,
        undefined,
        allOrders?.[0]?.flex_pack?.name || orders?.[0]?.flex_pack?.name,
        organization?.[0]?.name
      )
    } catch (err) {
      console.error('Failed to export all flex package holders', err)
    }
  }

  useEffect(() => {
    if (!didMountRef.current) return // ⛔ skip on first render
    runSearch() // ✅ run only for real changes
  }, [debouncedQuery, currentPage, perPage])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return
    runSearch(true) // True as in it is initial query
    didMountRef.current = true
    // run when org permissions are ready and flex packages management is allowed
  }, [orgPermissionsReady, hasPermission])

  useEffect(() => {
    setCurrentPage(0)
  }, [debouncedQuery])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          className="max-width-wrapper"
          data-testid="flex-pack-holders-section"
        >
          <Header
            organization={organization[0]}
            orders={orders}
            isInitialState={isInitialState}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onExport={handleExportAll}
          />
          <Results
            orders={orders}
            totalOrders={totalOrders}
            isInitialState={isInitialState}
            isLoading={isLoading}
            pagination={{
              currentPage,
              setCurrentPage,
              perPage,
              pageCount,
              setPageCount,
              resultsPerPageOpts,
              handleChange,
            }}
            runSearch={runSearch}
          />
        </section>
      </div>
    </>
  )
}

function Results({
  orders,
  totalOrders,
  isInitialState,
  isLoading,
  pagination,
  runSearch,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  // InitialState
  if (isInitialState && !orders?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No flex package holders</p>
          <p className="subtitle">This event has no flex package holders.</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (orders?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No results found...</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <div className="mb-4">
        <OrderInfo isLoading={isLoading} totalOrders={totalOrders} />
      </div>
      <Stack as="ul" gap={4}>
        {orders.map((order, i) => (
          <OrderSummary key={i} order={order} runSearch={runSearch} />
        ))}
      </Stack>
      <Pagination
        pageCount={pagination.pageCount}
        perPage={pagination.perPage}
        currentPage={pagination.currentPage}
        resultsPerPageOpts={pagination.resultsPerPageOpts}
        handleClick={(value) => pagination.setCurrentPage(value.selected)}
        handleChange={pagination.handleChange}
      />
    </>
  )
}

function Header({
  organization,
  orders,
  isInitialState,
  setSearchTerm,
  onExport,
}) {
  return (
    <SearchHeader
      selectOptions={{
        show: false,
      }}
      showActions={!isInitialState || orders?.length > 0}
      rightSideComponent={null}
      title="Flex package holders"
      desc="Comprehensive list of customers who have purchased flex packages"
      searchPlaceholder="Search by name or email"
      setSearchTerm={setSearchTerm}
      flexPackName={orders[0]?.flex_pack?.name}
      organizationName={organization?.name}
      downloadReport={onExport}
    />
  )
}
